import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { Flame, MapPin } from "lucide-react";

const PRODUCTION_URL = "https://smokey-af-bbq.emergent.host";

// All 22 items — same source as TableFlyer.jsx, kept independent so tweaks here
// don't disturb the 2-page flyer.
const ITEMS = [
  // $16 — Pizzas
  { img: "/menu/bbq_pizza.png", name: "Smokey AF Pizza", price: "$16" },
  { img: "/menu/nacho_pork_pizza.png", name: "Nacho Pork Pizza", price: "$16" },
  { img: "/menu/pepperoni_pizza.png", name: "Pepperoni Pizza", price: "$16" },
  { img: "/menu/chicken_alfredo_pizza.png", name: "Chicken Alfredo Pizza", price: "$16" },
  { img: "/menu/chicken_bacon_ranch_pizza.png", name: "Chicken Bacon Ranch", price: "$16" },
  // $14 — Signature mains
  { img: "/menu/bbq_boat_hero.png", name: "BBQ Boat", price: "$14" },
  {
    img: "https://static.prod-images.emergentagent.com/jobs/6ad5284b-01f5-432e-9dd7-8388ae8bdde3/images/ea2515a2333c003b27a647814b302029d3cce5e873cb14aabd10c36f28866c54.png",
    name: "Pulled Pork Sandwich",
    price: "$14",
  },
  {
    img: "https://static.prod-images.emergentagent.com/jobs/6ad5284b-01f5-432e-9dd7-8388ae8bdde3/images/7bf2ff7d38aa446f0d911781f9ff37801a21fc0c06fdec41228765bfa8ea3d62.png",
    name: "Japanese BBQ Burger",
    price: "$14",
  },
  { img: "/menu/loaded_pork_fries.png", name: "Loaded Pork Fries", price: "$14" },
  // $12 — Pretzel & Wings
  { img: "/menu/bbq_bavarian_pretzel.png", name: "Bavarian Behemoth Pretzel", price: "$12" },
  { img: "/menu/garlic_parmesan_wings.png", name: "Garlic Parm Wings", price: "$12" },
  { img: "/menu/bbq_wings.png", name: "BBQ Wings", price: "$12" },
  { img: "/menu/buffalo_wings.png", name: "Buffalo Wings", price: "$12" },
  { img: "/menu/chipotle_firecracker_wings.png", name: "Chipotle Firecracker Wings", price: "$12" },
  // $10 — Bowls & Sandwiches
  { img: "/menu/bbq_chicken_sandwich.png", name: "BBQ Chicken Sandwich", price: "$10" },
  { img: "/menu/pork_crack_mash_bowl.png", name: "Pork Crack Mash Bowl", price: "$10" },
  { img: "/menu/nacho_pork_sandwich.png", name: "Nacho Pork Sandwich", price: "$10" },
  { img: "/menu/japanese_pork_sandwich.png", name: "Japanese Pork Sandwich", price: "$10" },
  // $8 / $7 / $5 — Fusion, Minis, Sides
  { img: "/menu/bbq_sushi.png", name: "BBQ Sushi", price: "$8" },
  { img: "/menu/mini_pulled_pork_sandwich.png", name: "Mini Pulled Pork", price: "$7" },
  { img: "/menu/mini_bbq_boat.png", name: "Mini BBQ Boat", price: "$7" },
  { img: "/menu/order_of_fries.png", name: "Order of Fries", price: "$5" },
];

const SHEET_W = 816;
const SHEET_H = 1056;

function FoodTile({ item }) {
  return (
    <div className="bg-[#111] border border-white/10 overflow-hidden flex flex-col">
      <div
        className="bg-cover bg-center"
        style={{
          backgroundImage: `url(${item.img})`,
          aspectRatio: "1 / 0.52",
          filter: "saturate(1.12) contrast(1.05)",
        }}
      />
      <div
        className="flex items-center justify-between gap-1"
        style={{ padding: "6px 8px" }}
      >
        <div
          className="font-['Anton'] uppercase text-white tracking-wider leading-tight"
          style={{ fontSize: 12 }}
        >
          {item.name}
        </div>
        <div
          className="font-['Anton'] text-[#FF8C00] flex-shrink-0"
          style={{ fontSize: 20 }}
        >
          {item.price}
        </div>
      </div>
    </div>
  );
}

export function AllItemsPage() {
  return (
    <div
      className="relative bg-black overflow-hidden"
      style={{
        width: SHEET_W,
        height: SHEET_H,
        backgroundImage:
          "radial-gradient(ellipse 80% 45% at 50% 0%, rgba(255,59,48,0.18), transparent 60%), radial-gradient(ellipse 70% 40% at 50% 100%, rgba(255,140,0,0.14), transparent 60%)",
      }}
    >
      {/* Grain */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-25 mix-blend-screen"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.08 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* HEADER — very compact to leave room for 22 tiles */}
      <div className="relative px-8 pt-4 pb-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Flame
              className="text-[#FF3B30] drop-shadow-[0_0_10px_rgba(255,59,48,0.6)]"
              style={{ width: 18, height: 18 }}
            />
            <span
              className="font-['Anton'] uppercase text-white tracking-[0.3em]"
              style={{ fontSize: 14 }}
            >
              Smokey <span className="text-[#FF3B30]">AF</span> Barbecue
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <MapPin className="w-3 h-3 text-[#FF8C00]" />
            <span
              className="font-['Outfit'] uppercase text-[#FF8C00] tracking-[0.35em]"
              style={{ fontSize: 9 }}
            >
              Fremont, NE
            </span>
          </div>
        </div>

        <div className="text-center mt-1.5">
          <div
            className="inline-block bg-[#FF3B30] text-white font-['Anton'] uppercase tracking-[0.3em] rotate-[-1deg] shadow-[0_6px_18px_rgba(255,59,48,0.45)]"
            style={{ fontSize: 10, padding: "2px 10px" }}
          >
            The Whole Damn Menu
          </div>
          <h1
            className="font-['Anton'] uppercase text-white leading-[0.9] mt-1"
            style={{ fontSize: 34, letterSpacing: "-0.02em" }}
          >
            22 Reasons to <span style={{ color: "#FF3B30" }}>Order Up.</span>
          </h1>
          <p
            className="font-['Outfit'] italic text-zinc-300"
            style={{ fontSize: 10, marginTop: 1 }}
          >
            Wood-fired · Hand-pulled · Honest as hell.
          </p>
        </div>
      </div>

      {/* MENU GRID — 4 cols × 6 rows = 24 cells (22 food + QR spanning 2 cols) */}
      <div className="relative px-6 mt-2">
        <div className="grid grid-cols-4" style={{ gap: 6 }}>
          {ITEMS.map((item) => (
            <FoodTile key={item.name} item={item} />
          ))}

          {/* QR — spans 2 cols on the last row for prominence */}
          <div
            className="bg-[#FF3B30] border border-white/10 flex items-center overflow-hidden"
            style={{ gridColumn: "span 2", padding: "6px 10px" }}
          >
            <div className="bg-white flex-shrink-0" style={{ padding: 4 }}>
              <QRCodeSVG
                value={PRODUCTION_URL}
                size={82}
                level="H"
                bgColor="#ffffff"
                fgColor="#000000"
                includeMargin={false}
              />
            </div>
            <div className="flex-1 pl-3">
              <div
                className="font-['Outfit'] uppercase text-white/70 tracking-[0.25em]"
                style={{ fontSize: 7 }}
              >
                Order from your seat
              </div>
              <div
                className="font-['Anton'] uppercase text-white leading-tight mt-0.5"
                style={{ fontSize: 20, letterSpacing: "0.02em" }}
              >
                Scan.
                <br />
                Tap. <span className="text-black">Eat.</span>
              </div>
              <div
                className="font-['Outfit'] uppercase text-white/70 tracking-[0.2em] mt-0.5"
                style={{ fontSize: 7 }}
              >
                Pay card or cash at bar
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div className="absolute bottom-4 left-0 right-0 px-8">
        <div className="flex items-center justify-between">
          <div>
            <div
              className="font-['Outfit'] uppercase text-zinc-500 tracking-[0.3em]"
              style={{ fontSize: 8 }}
            >
              Order from your phone
            </div>
            <div
              className="font-['Anton'] uppercase text-white tracking-wider mt-0.5"
              style={{ fontSize: 12 }}
            >
              smokey-af-bbq.emergent.host
            </div>
          </div>
          <div className="text-right">
            <div
              className="font-['Outfit'] uppercase text-zinc-500 tracking-[0.3em]"
              style={{ fontSize: 8 }}
            >
              Inside Southern Style Bar
            </div>
            <div
              className="font-['Anton'] uppercase text-white tracking-wider mt-0.5"
              style={{ fontSize: 12 }}
            >
              648 N Broad St · Fremont, NE
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Chrome-free standalone page at full 8.5×11 resolution — used for PNG export.
export default function TableFlyerAll() {
  return (
    <div
      data-testid="tableflyer-all-standalone"
      className="min-h-screen w-full bg-black flex items-start justify-center"
      style={{ padding: 0 }}
    >
      <AllItemsPage />
    </div>
  );
}
